import { DefaultMap, hasOwnProperty } from "@glideapps/ts-necessities";
import type { ChangeObservable } from "common/change-obserable";
import { Watchable } from "common/change-obserable";
import { logError, logInfo } from "common/debug-print";
import { makeRowID } from "common/make-row-id";
import { frontendTrace, frontendTraceSync } from "common/tracing";
import { convertYesCodeValue, getYesCodeManifest, normalizeYesCodeURL, unloadYesCodeManifest } from "common/yes-code";
import type {
    YesCodeColumnManifest,
    YesCodeColumnRequest,
    YesCodeParameter,
    YesCodeValue,
} from "common/yes-code-types";
import { approvedYesCodeHost, yesCodeColumnResponse } from "common/yes-code-types";
import { isLeft } from "fp-ts/lib/Either";

const iframes = new DefaultMap<string, Promise<HTMLIFrameElement>>(addIFrame);

interface Module {
    run(...params: YesCodeParameter[]): YesCodeValue | Promise<YesCodeValue>;
}

const modules = new Map<string, Module>();

function addIFrame(url: string): Promise<HTMLIFrameElement> {
    return frontendTrace(
        "loadYesCode",
        { "yes_code.url": url },
        () =>
            new Promise(resolve => {
                const node = document.createElement("iframe");
                node.id = "iframe";
                node.src = normalizeYesCodeURL(url);
                node.style.display = "none";
                node.sandbox.value = "allow-scripts";
                node.onload = function () {
                    logInfo("loaded", url);

                    node.onload = null; // clear the handler, not to run it after the location change

                    resolve(node);
                };

                document.body.appendChild(node);
            }),
        "always"
    );
}

const resolvers = new Map<string, (v: YesCodeValue | undefined, e: string | undefined) => void>();
const errorObservables = new DefaultMap<string, Watchable<string | undefined>>(
    () => new Watchable<string | undefined>(undefined)
);

window.addEventListener("message", event => {
    const { origin, data } = event;

    const parsed = yesCodeColumnResponse.decode(data);
    if (isLeft(parsed)) {
        // logError("Could not parse yes-code response", data);
        return;
    }

    const { key, result, error } = parsed.right;
    logInfo("yes-code", origin, key, result, error);
    const resolve = resolvers.get(key);
    if (resolve === undefined) {
        logError("No resolver", key);
        return;
    }
    resolvers.delete(key);
    resolve(result?.value, error);
});

const callCounts = new DefaultMap<string, number>(() => 0);

function makeParams(manifest: YesCodeColumnManifest, paramsMap: ReadonlyMap<string, unknown>): YesCodeParameter[] {
    const params: YesCodeParameter[] = [];
    for (const ps of manifest.params) {
        params.push({ name: ps.name, ...convertYesCodeValue(paramsMap.get(ps.name), ps.type) });
    }
    return params;
}

async function importModuleAsync(url: string): Promise<Module | undefined> {
    try {
        const maybeModule = await import(/* webpackIgnore: true */ url + "/function.js");
        if (!hasOwnProperty(maybeModule, "default")) return undefined;
        if (!hasOwnProperty(maybeModule.default, "run")) return undefined;
        const module = { run: maybeModule.default.run } as Module;
        modules.set(url, module);
        return module;
    } catch (e: unknown) {
        logError("Could not load module", url, e);
        return undefined;
    }
}

function importModule(url: string): Module | Promise<Module | undefined> {
    const maybeModule = modules.get(url);
    if (maybeModule !== undefined) {
        return maybeModule;
    }
    return importModuleAsync(url);
}

async function traceAsync<T>(url: string, f: () => Promise<T>): Promise<T> {
    let count = callCounts.update(url, id => id + 1);
    if ((count & (count - 1)) === 0) {
        // `count` will be a power of two here, and it's the number of calls
        // total to this URL so far, but we only want to count half of them,
        // since we've already counted the other half.  The only exception is
        // when `count` is 1, which is why we need the max to give 1.
        count = Math.max(1, count / 2);

        return frontendTrace("invokeYesCode", { "yes_code.url": url, "yes_code.count": count }, f, "always");
    } else {
        return f();
    }
}

function traceSync<T>(url: string, f: () => T): T {
    let count = callCounts.update(url, id => id + 1);
    if ((count & (count - 1)) === 0) {
        // `count` will be a power of two here, and it's the number of calls
        // total to this URL so far, but we only want to count half of them,
        // since we've already counted the other half.  The only exception is
        // when `count` is 1, which is why we need the max to give 1.
        count = Math.max(1, count / 2);

        return frontendTraceSync("invokeYesCode", { "yes_code.url": url, "yes_code.count": count }, f, "always");
    } else {
        return f();
    }
}

async function callDirectYesCodeAsync(
    url: string,
    paramsMap: ReadonlyMap<string, unknown>
): Promise<YesCodeValue | undefined> {
    const [module, manifest] = await Promise.all([importModule(url), getYesCodeManifest(url)]);
    if (typeof manifest === "string" || module === undefined) return undefined;

    if (manifest.released !== "direct") {
        return callYesCodeAsync(url, paramsMap);
    }

    const params = makeParams(manifest, paramsMap);

    const resultValue = await traceAsync(url, async () => module.run(...params));
    return convertYesCodeValue(resultValue, manifest.result.type).value;
}

function callDirectYesCode(
    url: string,
    paramsMap: ReadonlyMap<string, unknown>
): YesCodeValue | undefined | Promise<YesCodeValue | undefined> {
    const manifest = getYesCodeManifest(url);
    if (typeof manifest === "string") return undefined;

    if (manifest instanceof Promise) {
        return callDirectYesCodeAsync(url, paramsMap);
    } else if (manifest.released !== "direct") {
        return callYesCodeAsync(url, paramsMap);
    }

    const module = importModule(url);
    if (module instanceof Promise) {
        return callDirectYesCodeAsync(url, paramsMap);
    }

    const params = makeParams(manifest, paramsMap);

    // NOTE: In the case where this returns a promise, the duration will not
    // be accurate because we're not awaiting it inside the trace.
    return traceSync(url, () => {
        try {
            const result = module.run(...params);
            if (result instanceof Promise) {
                return result.then(v => convertYesCodeValue(v, manifest.result.type).value).catch(() => undefined);
            } else {
                return convertYesCodeValue(result, manifest.result.type).value;
            }
        } catch {
            return undefined;
        }
    });
}

async function callYesCodeAsync(
    url: string,
    paramsMap: ReadonlyMap<string, unknown>
): Promise<YesCodeValue | undefined> {
    const [iframe, maybeManifest] = await Promise.all([iframes.get(url), getYesCodeManifest(url)]);

    if (typeof maybeManifest === "string") return undefined;
    const manifest = maybeManifest;

    const { contentWindow: maybeWindow } = iframe;
    if (maybeWindow === null) return undefined;
    const contentWindow = maybeWindow;

    const key = makeRowID();
    const params = makeParams(manifest, paramsMap);
    const request: YesCodeColumnRequest = { key, params };

    function makePromise(): Promise<YesCodeValue | undefined> {
        return new Promise(resolve => {
            resolvers.set(key, (resultValue, error) => {
                const result = convertYesCodeValue(resultValue, manifest.result.type);
                if (error !== undefined) {
                    errorObservables.get(url).current = error;
                }
                resolve(result.value);
            });
            contentWindow.postMessage(request, "*");
        });
    }

    return traceAsync(url, makePromise);
}

export function callYesCode(
    url: string,
    paramsMap: ReadonlyMap<string, unknown>
): YesCodeValue | undefined | Promise<YesCodeValue | undefined> {
    const urlObject = new URL(url);
    if (urlObject.host.toLowerCase() === approvedYesCodeHost.toLowerCase()) {
        return callDirectYesCode(url, paramsMap);
    }
    return callYesCodeAsync(url, paramsMap);
}

export function getYesCodeErrorChangeObservable(url: string): ChangeObservable<string | undefined> {
    return errorObservables.get(url);
}

export function unloadYesCode(url: string): void {
    if (iframes.has(url)) {
        const iframe = iframes.get(url);
        iframes.delete(url);
        void iframe.then(e => e.remove());
    }
    if (errorObservables.has(url)) {
        errorObservables.get(url).current = undefined;
    }
    unloadYesCodeManifest(url);
}
